import { useContext, type ReactElement, type ReactNode } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Calendar } from 'lucide-react';
import {
  formatRelativeTime,
  formatRelativeTimeFuzzy,
  formatUtcDateTime,
} from '@/lib/date-utils';
import {
  TimestampFormatContext,
  type TimestampFormat,
} from '@/contexts/TimestampFormatContext';

/**
 * Which relative-time formatter the inline/tooltip relative form uses:
 * - `minutes` (default): minute precision via {@link formatRelativeTime}.
 * - `fuzzy`: lossy "less than …" ladder via {@link formatRelativeTimeFuzzy},
 *   for screens whose data is not accurate to the minute.
 * Only meaningful when the effective format is `relative`.
 */
type RelativeTimeVariant = 'minutes' | 'fuzzy';

interface RelativeTimeWithTooltipProps {
  date: Date | string;
  showIcon?: boolean;
  /**
   * `relative` shows a humanized delta; `absolute` shows the fixed UTC
   * timestamp. When omitted, the effective format comes from
   * `TimestampFormatContext` (the per-user Timestamp Format setting); an
   * explicit prop always wins as an override.
   */
  format?: TimestampFormat;
  /**
   * Selects the relative-time formatter. Defaults to `minutes` (exact minute
   * precision). Pass `fuzzy` on screens whose timestamps are not accurate to
   * the minute so they never show an exact minute count below the hour.
   */
  variant?: RelativeTimeVariant;
  /**
   * Render the trigger as an inline `<span>` instead of the default block-level
   * flex `<div>`, so it nests validly inside an inline context (e.g. an inline
   * metadata chip whose parent is itself a `<span>`).
   */
  inline?: boolean;
  /**
   * Node rendered before the timestamp text inside the trigger (e.g. a
   * freshness dot). When provided it replaces the default `showIcon` Calendar
   * glyph, so the caller owns the leading visual entirely.
   */
  leading?: ReactNode;
}

/**
 * Displays a timestamp with a hover tooltip. The inline form follows the
 * effective format (explicit prop > per-user context setting > relative
 * default); the tooltip shows the OPPOSITE form. Relative mode: relative inline
 * + absolute UTC on hover. Absolute mode: absolute UTC inline + relative ("ago")
 * on hover. The inline truncate-containment span keeps a long absolute string
 * from bleeding into the neighbouring column as a cheap narrow-viewport net.
 */
export function RelativeTimeWithTooltip({
  date,
  showIcon = true,
  format,
  variant = 'minutes',
  inline = false,
  leading,
}: RelativeTimeWithTooltipProps): ReactElement {
  const { value: contextFormat } = useContext(TimestampFormatContext);
  const effectiveFormat = format ?? contextFormat;

  // Pass the raw value (not a locally pre-parsed `new Date(date)`) to the
  // formatters: they read timezone-less backend strings as UTC, so a
  // pre-parse here would re-introduce the local-offset shift in both the
  // inline relative text and the UTC tooltip.
  const relative =
    variant === 'fuzzy'
      ? formatRelativeTimeFuzzy(date)
      : formatRelativeTime(date);
  const absolute = formatUtcDateTime(date);
  const isAbsolute = effectiveFormat === 'absolute';
  const display = isAbsolute ? absolute : relative;
  const tooltipText = isAbsolute ? relative : absolute;

  // `inline` swaps the trigger container to a <span> so it nests validly inside
  // inline contexts (a <div> inside a <span> chip is invalid HTML); inline-flex
  // keeps the icon + text aligned. `leading` lets the caller own the leading
  // glyph (e.g. a freshness dot) in place of the default Calendar icon.
  const Container = inline ? 'span' : 'div';
  const containerClass = inline
    ? 'inline-flex min-w-0 items-center gap-1'
    : 'flex min-w-0 items-center gap-1';
  const leadingNode =
    leading ?? (showIcon ? <Calendar className="h-3 w-3 shrink-0" /> : null);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Container className={containerClass}>
            {leadingNode}
            {/* overflow-hidden truncate span: a long absolute string clips
                with an ellipsis instead of overflowing into the neighbouring
                column. The full value stays recoverable via the tooltip. */}
            <span
              data-testid="relative-time-value"
              className="min-w-0 truncate"
            >
              {display}
            </span>
          </Container>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
